import type { ReactNode } from 'react'

/**
 * Justification, one click away and never one fetch away.
 *
 * # The defect this exists to fix
 *
 * An operator opened Customer 360's price-list bands and said the screen
 * "looks overwhelming". They were right, and the measurement is unambiguous:
 * two `kv` blocks delivering **nine values** carried **about 1,300 characters**
 * of permanently visible explanation between them. The screen was spending
 * most of its area saying why a number was correct, and the number itself —
 * `default v1` — was the smallest thing on it.
 *
 * The prose is not the problem. It is why these screens are trustworthy, and
 * several of those paragraphs are the only place a subtle rule is written down.
 * The problem is that **a justification and a value had equal weight**, and the
 * justification had more area.
 *
 * # `<details>` rather than a state hook, and that is a decision
 *
 *   - **The content is always in the DOM.** Ctrl-F finds it closed. A print
 *     stylesheet can force every one open. A screen reader reaches it. If this
 *     were a `useState` swap the console would have quietly lost its own
 *     documentation on every surface that is not a mouse.
 *   - **Keyboard reachable for free**, with no `tabIndex`, no key handler, and
 *     no ARIA to get wrong.
 *   - **No fetch on open.** Nothing here is lazy. An explanation that arrives
 *     after a click is an explanation somebody stops clicking for.
 *
 * # What must never go behind this
 *
 * Anything that qualifies **whether a value is true** — a provenance stamp, a
 * staleness age, a source badge, a tone chip. Those are not explanation; they
 * are part of the claim, and a value whose qualifier is one click away is a
 * value being reported without its qualifier.
 *
 * The rule the callers apply: **collapse why-it-is-correct, keep what-it-is
 * and whether-to-believe-it.** `Kv` draws that line by note length, below.
 *
 * `Note` blocks — 344 of them — are a SEPARATE change, deliberately not batched
 * here. Their line is TONE, not length: a neutral note is justification, and an
 * amber or red one is the thing the screen exists to say. Collapsing
 * *"unconfirmed — NOT clear"* or *"an empty queue does NOT mean the book is
 * clean"* would be a worse defect than the density it fixed, and an invisible
 * one — the screen would look calm and be wrong by omission.
 */
export function Why({
  label = 'why', children, indent = false,
}: {
  /** What the closed affordance says. Terse — it is a control, not a heading. */
  readonly label?: string
  readonly children: ReactNode
  /** Aligns the open body with a parent that already has a text column. */
  readonly indent?: boolean
}) {
  return (
    <details
      style={{ marginTop: '3px' }}
      // A hook for a print stylesheet to force open, and for a test to find
      // every collapsed explanation on a screen without knowing its wording.
      data-why=""
    >
      <summary
        style={{
          cursor: 'pointer',
          fontSize: '10px',
          letterSpacing: '.05em',
          textTransform: 'uppercase',
          color: 'var(--k-mut2)',
          fontFamily: 'var(--k-mono)',
          // The marker is the affordance; without a width the summary box
          // stretches and the click target becomes the whole row.
          width: 'fit-content',
        }}
      >
        {label}
      </summary>
      <div
        style={{
          fontSize: '11px',
          color: 'var(--k-mut)',
          marginTop: '3px',
          maxWidth: '82ch',
          textWrap: 'pretty',
          ...(indent ? { paddingLeft: '2px' } : {}),
        }}
      >
        {children}
      </div>
    </details>
  )
}
